import { Injectable } from '@angular/core';

import {
  FAQ_ENTRIES,
  FaqEntry,
  INFO_QUERIES,
  InfoQueryDefinition,
  InfoQueryMatch
} from '../models/chatbot-knowledge';

export type ChatbotMatch =
  | { kind: 'faq'; entry: FaqEntry }
  | { kind: 'info'; query: InfoQueryMatch };

@Injectable({
  providedIn: 'root'
})
export class ChatbotQueryService {
  // Matching is keyword based rather than a call out to the analysis service,
  // so questions are answered on every environment and without a round trip.
  // A group matches when all of its words are present; the best match is the
  // most specific group, so a precise question wins over a general one that
  // happens to share a word with it.

  // How-to answers and live lookups are scored against each other in one pass
  // rather than one type being tried before the other. Checking lookups first
  // would let "how do I override a license plate?" match the license plate
  // lookup on the words it shares, and never reach the answer it was asking
  // for.
  match(text: string, grantedPermissions: string[]): ChatbotMatch | null {
    const faq = this.bestMatch(
      this.visible(FAQ_ENTRIES, grantedPermissions),
      text,
      (entry) => entry.keywords
    );
    const info = this.bestMatch(
      this.visible(INFO_QUERIES, grantedPermissions),
      text,
      (entry) => entry.keywords
    );

    if (faq && (!info || faq.score >= info.score)) {
      return { kind: 'faq', entry: faq.entry };
    }
    if (info) {
      return { kind: 'info', query: this.toInfoMatch(info.entry, text) };
    }
    return null;
  }

  matchFaq(text: string, grantedPermissions: string[]): FaqEntry | null {
    const match = this.match(text, grantedPermissions);
    return match?.kind === 'faq' ? match.entry : null;
  }

  matchInfoQuery(
    text: string,
    grantedPermissions: string[]
  ): InfoQueryMatch | null {
    const match = this.match(text, grantedPermissions);
    return match?.kind === 'info' ? match.query : null;
  }

  private toInfoMatch(
    definition: InfoQueryDefinition,
    text: string
  ): InfoQueryMatch {
    if (!definition.takesArgument) {
      return { kind: definition.kind };
    }
    const argument = this.extractArgument(text);
    return argument
      ? { kind: definition.kind, argument }
      : { kind: definition.kind };
  }

  faqsFor(grantedPermissions: string[]): FaqEntry[] {
    return this.visible(FAQ_ENTRIES, grantedPermissions);
  }

  infoQueriesFor(grantedPermissions: string[]): InfoQueryDefinition[] {
    return this.visible(INFO_QUERIES, grantedPermissions);
  }

  // Anything without a permission is open to everyone; anything with one is
  // hidden rather than refused, so a processor is never offered a question
  // whose answer they are not allowed to see.
  private visible<T extends { permission?: string }>(
    entries: T[],
    grantedPermissions: string[]
  ): T[] {
    return entries.filter(
      (entry) => !entry.permission || grantedPermissions.includes(entry.permission)
    );
  }

  private bestMatch<T>(
    entries: T[],
    text: string,
    keywordsOf: (entry: T) => string[][]
  ): { entry: T; score: number } | null {
    const haystack = (text || '').toLowerCase();
    if (!haystack.trim()) {
      return null;
    }

    let best: { entry: T; score: number } | null = null;

    for (const entry of entries) {
      for (const group of keywordsOf(entry)) {
        if (group.length === 0) {
          continue;
        }
        if (!group.every((word) => haystack.includes(word))) {
          continue;
        }
        // Longer, wordier groups are more specific, so they win. Ties keep the
        // entry declared first, which is the order the chips are shown in.
        const score = group.reduce((sum, word) => sum + word.length, 0);
        if (!best || score > best.score) {
          best = { entry, score };
        }
      }
    }

    return best;
  }

  // Words that can trail a lookup without naming anything to look for, so
  // "show all license plates please" lists them rather than searching for
  // "please".
  private static readonly NON_ARGUMENTS = new Set([
    'please',
    'now',
    'here',
    'thanks',
    'thank you'
  ]);

  // Pulls the search term out of a lookup such as "find license plate red bin
  // 4" or "license plate details for LP-22". Matching plates? matters: without
  // it "show all license plates" captures the trailing s and searches for it.
  private extractArgument(text: string): string | null {
    const match = (text || '')
      .toLowerCase()
      .match(
        /license\s+plates?\b\s*(?:info(?:rmation)?|details?)?\s*(?:for|named|called|number|#|:)?\s*(.*)$/i
      );
    if (!match) {
      return null;
    }
    const argument = match[1].trim().replace(/[.?!]+$/, '').trim();
    if (
      argument.length === 0 ||
      ChatbotQueryService.NON_ARGUMENTS.has(argument)
    ) {
      return null;
    }
    return argument;
  }
}
